import InfoIcon from '@mui/icons-material/Info';
import { useMemo } from 'react';

import { ISummary, TAIForm } from 'src/types';

export const formatAIFormStatus = (school: ISummary) => {
  switch (school.processStatus) {
    case 'submitted':
      return (
        <>
          <span className="font-bold">Status:</span> Submitted <InfoIcon />
        </>
      );

    case 'appeal':
    case 'opsApproved':
    case 'international':
    case 'testSecurity':
    case 'processing':
    case 'xpmDenied':
    case 'deactivationApproval':
      return (
        <>
          <span className="font-bold">Status:</span> Under Review <InfoIcon />
        </>
      );

    case 'reopen':
      return (
        <>
          <div>
            <span className="font-bold">Status:</span> Requires more information <InfoIcon />
          </div>
          {school.processStatusComments && (
            <div className="div-wordwrap">
              <span className="font-bold">Additional Information Required:</span> {school.processStatusComments}.
            </div>
          )}
          {school?.requireTestingPlanUpdates === 'true' && (
            <div className="div-wordwrap font-bold">Testing Plan Updates Required.</div>
          )}
        </>
      );

    // case 'appeal':
    //   return (
    //     <>
    //       <span className="font-bold">Status:</span> Pending Appeal <InfoIcon />
    //     </>
    //   );

    // case 'international':
    //   return (
    //     <>
    //       <span className="font-bold">Status:</span> Pending International <InfoIcon />
    //     </>
    //   );

    // case 'testSecurity':
    //   return (
    //     <>
    //       <span className="font-bold">Status:</span> Pending Test Security <InfoIcon />
    //     </>
    //   );

    // case 'opsApproved':
    //   return (
    //     <>
    //       <span className="font-bold">Status:</span> OPS Approved <InfoIcon />
    //     </>
    //   );

    case 'rejected':
      return (
        <>
          <div>
            <span className="font-bold">Status:</span> Rejected <InfoIcon />
          </div>
          {school.processStatusComments && (
            <div className="div-wordwrap">
              <span className="font-bold">Denial Reason:</span> {school.processStatusComments}.
            </div>
          )}
        </>
      );

    case 'completed':
      return (
        <>
          <div>
            <span className="font-bold">Status:</span> Completed{' '}
          </div>
          {school?.aiCode && (
            <div>
              <span className="font-bold">School Code:</span>
              {school.aiCode}.
            </div>
          )}
          {school?.diCode && (
            <div>
              <span className="font-bold">Institution Code:</span>
              {school.diCode}.
            </div>
          )}
          {school?.aiLevelInd && (
            <div>
              <span className="font-bold">Approved School Level:</span>
              {school.aiLevelInd}.
            </div>
          )}
          {school.processStatusComments && (
            <div className="div-wordwrap">
              <span className="font-bold">Reviewer Comments:</span>
              {school.processStatusComments}.
            </div>
          )}
        </>
      );

    case 'started':
      return (
        <>
          <span className="font-bold">Status:</span> Started <InfoIcon />
        </>
      );

    default:
      return null;
  }
};

export const formatTooltipTextByStatus = (status: string, requireTestingPlanUpdates?: boolean) => {
  switch (status) {
    case 'submitted':
      return 'Application received but awaiting review';

    case 'processing':
      return 'Application is under review';

    case 'reopen':
      if (requireTestingPlanUpdates) {
        return 'Application review is on hold pending submission of Test Plan requested';
      }
      return 'Application review is on hold pending submission of outstanding information requested';

    case 'rejected':
      return 'Unable to assign a College Board Code for application reviewed';

    case 'completed':
      return 'completed';

    case 'started':
      return 'Application process started';

    case 'appeal':
      return 'Additional review is required by this team for approval';

    case 'international':
      return 'Additional review is required by this team for approval';

    case 'testSecurity':
      return 'Additional review is required by this team for approval';

    case 'opsApproved':
      return 'Code Request has passed the first approval steps';

    default:
  }
};

const useAIFormStatus = (school: ISummary) => {
  const status = useMemo(() => {
    return formatAIFormStatus(school);
  }, [school]);

  const statusTooltip = useMemo(() => {
    return formatTooltipTextByStatus(school.processStatus, school?.requireTestingPlanUpdates === 'true');
  }, [school]);

  return {
    status,
    statusTooltip,
  };
};

export default useAIFormStatus;
